/**
 * Bank transfer and agent address cards (振込先・代理人住所). UP System only.
 * Read-only display; no API calls.
 *
 * Data source (both sections):
 * - API: GET .../clinic-informations/init → data.clinic_information
 *   (Bank/representative fields are only included when is_upsystem is true, i.e. tenant URL is upsystem.)
 * - Backend: table clinic_informations (latest row by clinic_id); columns:
 *   - 振込先: bank_code, bank_name, bank_name_kana, bank_branch_code, bank_branch_name, bank_branch_name_kana, account_number, account_name, account_name_kana, account_type_name
 *   - 代理人住所: representative_name, representative_address, representative_telephone
 * If values are empty, the DB row for that clinic has nulls; fill clinic_informations in DB or check is_upsystem in init response.
 */

import React from 'react';
import { CollapsibleCard, InfoRow, TwoColumnInfoRow } from '@/components/molecules';
import type { ClinicInformation } from './types';

export interface ClinicInformationBankSectionProps {
  clinicInfo: ClinicInformation;
}

const ClinicInformationBankSection: React.FC<ClinicInformationBankSectionProps> = ({
  clinicInfo,
}) => (
  <>
    <CollapsibleCard title="振込先">
      <div className="space-y-2">
        <InfoRow label="支払区分" value={clinicInfo.bank_payment_type_name} />
        <TwoColumnInfoRow
          label1="金融機関コード"
          value1={clinicInfo.bank_code}
          label2="銀行区分"
          value2={clinicInfo.bank_type_name}
        />
        <TwoColumnInfoRow
          label1="金融機関名"
          value1={clinicInfo.bank_name}
          label2="金融機関名カナ"
          value2={clinicInfo.bank_name_kana}
        />
        <TwoColumnInfoRow
          label1="支店コード"
          value1={clinicInfo.bank_branch_code}
          label2="支店区分"
          value2={clinicInfo.bank_branch_type_name}
        />
        <TwoColumnInfoRow
          label1="支店名"
          value1={clinicInfo.bank_branch_name}
          label2="支店名カナ"
          value2={clinicInfo.bank_branch_name_kana}
        />
        <TwoColumnInfoRow
          label1="預金の種類"
          value1={clinicInfo.bank_account_type_name ?? clinicInfo.account_type_name}
          label2="口座番号"
          value2={clinicInfo.account_number}
        />
        <TwoColumnInfoRow
          label1="口座名義"
          value1={clinicInfo.account_name}
          label2="口座名義カナ"
          value2={clinicInfo.account_name_kana}
        />
        <InfoRow label="口座種別" classNameLabel="mr-[-3px]" value={clinicInfo.account_type_name} />
      </div>
    </CollapsibleCard>

    <CollapsibleCard title="代理人住所">
      <div className="space-y-1">
        <InfoRow label="住所" value={clinicInfo.representative_address} />
        <TwoColumnInfoRow
          label1="氏名"
          value1={clinicInfo.representative_name}
          label2="電話番号"
          value2={clinicInfo.representative_telephone}
        />
      </div>
    </CollapsibleCard>
  </>
);

export default ClinicInformationBankSection;
